import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts'
import { Activity } from 'lucide-react'
import { getActivityPulse } from '../../services/api'
import PeriodToggle from './PeriodToggle'

// Owner-view plan, Session 2.1 — per-day event counts stacked by event_type.
// Zero-count days render explicitly so silent days are visible. React Query
// keeps refetching every 60s to match the Action Required strip cadence.

const EVENT_COLORS = {
  weighing: '#10b981',
  treatment: '#8b5cf6',
  vaccination: '#3b82f6',
  observation: '#64748b',
  calving: '#22c55e',
  death: '#ef4444',
  sale: '#f59e0b',
  lost: '#f97316',
  artificial_insemination: '#ec4899',
  pregnancy_check_positive: '#14b8a6',
  heat_detected: '#f472b6',
}

const FALLBACK_COLOR = '#94a3b8'

const PERIOD_OPTIONS = [
  { value: 7, label: '7d' },
  { value: 30, label: '30d' },
  { value: 90, label: '90d' },
]

function dayLabel(iso, days) {
  const d = new Date(iso)
  if (days >= 90) {
    return `${d.getMonth() + 1}/${d.getDate()}`
  }
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

export default function ActivityPulseCard({ ranchId }) {
  const storageKey = `dashboard.activityPulse.days.${ranchId}`
  const [days, setDays] = useState(() => {
    try {
      const stored = parseInt(localStorage.getItem(storageKey) || '', 10)
      return [7, 30, 90].includes(stored) ? stored : 7
    } catch { return 7 }
  })

  const setAndPersist = (next) => {
    setDays(next)
    try { localStorage.setItem(storageKey, String(next)) } catch { /* localStorage may be disabled (e.g. private mode) */ }
  }

  const { data, isLoading } = useQuery({
    queryKey: ['activityPulse', ranchId, days],
    queryFn: ({ signal }) => getActivityPulse(ranchId, { days, signal }),
    staleTime: 60_000,
    refetchInterval: 60_000,
  })

  const { chartData, eventTypes, totalEvents, silentDays } = useMemo(() => {
    if (!data) return { chartData: [], eventTypes: [], totalEvents: 0, silentDays: 0 }
    const types = (data.event_types_present || []).filter(
      (et) => (data.buckets || []).some((b) => (b[et] || 0) > 0)
    )
    const chart = (data.buckets || []).map((b) => ({
      ...b,
      label: dayLabel(b.day, data.days),
    }))
    let total = 0
    let silent = 0
    for (const b of chart) {
      const dayTotal = types.reduce((sum, et) => sum + (b[et] || 0), 0)
      total += dayTotal
      if (dayTotal === 0) silent += 1
    }
    return { chartData: chart, eventTypes: types, totalEvents: total, silentDays: silent }
  }, [data])

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
          <Activity className="h-5 w-5 text-primary-600" />
          Activity Pulse
        </h3>
        <PeriodToggle
          ariaLabel="Activity pulse period"
          options={PERIOD_OPTIONS}
          value={days}
          onChange={setAndPersist}
        />
      </div>

      {isLoading || !data ? (
        <div className="h-56 animate-pulse bg-gray-50 rounded" />
      ) : totalEvents === 0 ? (
        <div className="h-56 flex flex-col items-center justify-center text-gray-500 text-sm">
          <Activity className="h-10 w-10 mb-2 opacity-30" />
          <p>No events logged in the last {days} days.</p>
          <p className="text-xs mt-1 text-gray-400">Expected: weighings, treatments, observations.</p>
        </div>
      ) : (
        <>
          <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-gray-600 mb-3">
            <span><strong className="text-gray-900">{totalEvents}</strong> events total</span>
            <span><strong className={silentDays > 0 ? 'text-amber-700' : 'text-gray-900'}>{silentDays}</strong> silent day{silentDays === 1 ? '' : 's'}</span>
          </div>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 11, fill: '#6b7280' }}
                  tickLine={false}
                  interval={days >= 90 ? 6 : days >= 30 ? 2 : 0}
                />
                <YAxis
                  allowDecimals={false}
                  tick={{ fontSize: 11, fill: '#6b7280' }}
                  tickLine={false}
                  axisLine={false}
                />
                <Tooltip
                  contentStyle={{ fontSize: 12 }}
                  cursor={{ fill: 'rgba(0,0,0,0.04)' }}
                />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {eventTypes.map((et) => (
                  <Bar
                    key={et}
                    dataKey={et}
                    stackId="events"
                    fill={EVENT_COLORS[et] || FALLBACK_COLOR}
                    name={et.replace(/_/g, ' ')}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </>
      )}
    </div>
  )
}
